import React, { useCallback, useEffect, useState } from 'react';
import { CountryCodeInput } from '../../../shared/components/CountryFlag';
import {
    Alert, Button, Card, Checkbox, DatePicker, Form, InputNumber, Input, Modal, Popconfirm, Select, Space,
    Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../../shared/services/apiClient';
import PageHeader from '../../../shared/components/PageHeader';
import { registrarService } from '../../services/registrarService';
import GiftRulesModal from '../../components/domain/GiftRulesModal';

const { Text } = Typography;
const OPS = { register: '注册', renew: '续费', transfer: '转入' };
const STATE = {
    current: { color: 'green', label: '进行中' },
    upcoming: { color: 'blue', label: '未开始' },
    ended: { color: 'default', label: '已结束' },
    disabled: { color: 'default', label: '已停用' },
};

const api = {
    get: (url, params) => apiClient.get(`/api/admin${url}`, { params }).then((r) => r.data),
    post: (url, body) => apiClient.post(`/api/admin${url}`, body).then((r) => r.data),
};
const errText = (e) => e?.response?.data?.error || e?.error || e?.message || '操作失败';

/* ── 加价策略 ─────────────────────────────────────────────── */
const PolicyTab = ({ settings, reload }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [cost, setCost] = useState(10);
    const pct = Form.useWatch('markup_percentage', form);
    const min = Form.useWatch('markup_min', form);
    const preview = cost > 0 ? +(cost + Math.max(cost * (Number(pct) || 0) / 100, Number(min) || 0)).toFixed(2) : 0;

    useEffect(() => {
        if (settings) form.setFieldsValue(settings);
    }, [settings, form]);

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await api.post('/domain/pricing/settings', v);
            if (r.success) {
                message.success(r.data.repriced ? `已保存，${r.data.repriced} 个价格已更新` : '已保存');
                reload();
            } else message.error(r.error || '保存失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card bordered={false}>
            <Form form={form} layout="vertical" style={{ maxWidth: 520 }}>
                <Space size={16} wrap>
                    <Form.Item label="默认加价比例 (%)" name="markup_percentage" rules={[{ required: true }]}>
                        <InputNumber min={0} max={500} precision={2} style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item label="每年最低利润" name="markup_min" rules={[{ required: true }]} tooltip="注册商币种（多为 USD）">
                        <InputNumber min={0} precision={2} style={{ width: 160 }} />
                    </Form.Item>
                </Space>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={(
                        <Space wrap>
                            <span>售价 = 成本 + max(成本×比例, 最低利润)。成本</span>
                            <InputNumber size="small" min={0} precision={2} value={cost} onChange={(v) => setCost(Number(v) || 0)} style={{ width: 90 }} />
                            <span>→ 售价 <b>{preview}</b></span>
                        </Space>
                    )}
                />
                <Space size={16} wrap>
                    <Form.Item label="每日自动同步价格" name="sync_enabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item label="成本降幅超过 (%) 时待审" name="review_pct" tooltip="成本上涨立即生效；大幅下降先保持原售价，待审核">
                        <InputNumber min={0} max={100} style={{ width: 160 }} />
                    </Form.Item>
                </Space>
                <Space>
                    <Button type="primary" loading={saving} onClick={save}>保存并重算价格</Button>
                    <Text type="secondary">
                        单独后缀的加价在「域名后缀定价」里设置 · 已覆盖 {settings?.overrides ?? 0} 个
                        {settings?.manual_rows ? ` · 手动定价 ${settings.manual_rows} 个` : ''}
                        {settings?.loss_tripped ? <Text type="danger">{` · 亏本已下架 ${settings.loss_tripped} 个`}</Text> : null}
                        {settings?.last_synced_at ? ` · 上次同步 ${settings.last_synced_at}` : ''}
                    </Text>
                </Space>
            </Form>
        </Card>
    );
};

/* ── 特价活动 ─────────────────────────────────────────────── */
const PromoModal = ({ open, editing, registrars, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        if (editing) {
            form.setFieldsValue({
                ...editing,
                tlds: [editing.tld],
                price: Number(editing.price),
                first_year_only: !!editing.first_year_only,
                range: [dayjs(editing.starts_at), dayjs(editing.ends_at)],
            });
        } else {
            form.setFieldsValue({ operation: 'register', first_year_only: true, range: [dayjs(), dayjs().add(30, 'day')] });
        }
    }, [open, editing, form]);

    const submit = async (allowBelowCost = false) => {
        const v = await form.validateFields();
        const body = {
            id: editing?.id,
            name: v.name,
            tlds: v.tlds,
            registrar_id: v.registrar_id || null,
            operation: v.operation,
            price: v.price,
            first_year_only: v.first_year_only,
            max_years: v.first_year_only ? null : (v.max_years || null),
            starts_at: v.range[0].format('YYYY-MM-DD HH:mm:ss'),
            ends_at: v.range[1].format('YYYY-MM-DD HH:mm:ss'),
            allow_below_cost: allowBelowCost,
        };
        setSaving(true);
        try {
            const r = await api.post('/domain/promotions/save', body);
            if (r.success) {
                const tripped = r.data?.tripped || [];
                if (tripped.length) message.warning(`已保存；${tripped.map((t) => '.' + t.tld).join(' ')} 因亏本已自动下架`);
                else message.success('已保存');
                onSaved();
            } else message.error(r.error || '保存失败');
        } catch (e) {
            const d = e?.response?.data;
            if (d?.code === 'BELOW_COST') {
                Modal.confirm({
                    title: '特价低于成本',
                    content: <div>{(d.warnings || []).map((w) => <div key={w}>{w}</div>)}</div>,
                    okText: '仍然保存',
                    onOk: () => submit(true),
                });
            } else message.error(errText(e));
        } finally {
            setSaving(false);
        }
    };

    const firstYearOnly = Form.useWatch('first_year_only', form);

    return (
        <Modal
            title={editing ? '编辑特价' : '新建特价'}
            open={open}
            onCancel={onClose}
            onOk={() => submit(false)}
            confirmLoading={saving}
            okText="保存"
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item label="活动名称" name="name" rules={[{ required: true, message: '请填写名称' }]}>
                    <Input maxLength={100} placeholder="如：.xyz 首年特惠" />
                </Form.Item>
                <Form.Item label="后缀" name="tlds" rules={[{ required: true, message: '请输入后缀' }]} tooltip="可输入多个，每个后缀单独建一条活动">
                    <Select mode="tags" tokenSeparators={[',', ' ']} disabled={!!editing} placeholder="xyz、site…" />
                </Form.Item>
                <Space size={16} wrap>
                    <Form.Item label="类型" name="operation">
                        <Select style={{ width: 120 }} options={Object.entries(OPS).map(([value, label]) => ({ value, label }))} />
                    </Form.Item>
                    <Form.Item label="特价 / 年" name="price" rules={[{ required: true, message: '请填写价格' }]}>
                        <InputNumber min={0.01} precision={2} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item label="注册商" name="registrar_id" tooltip="留空 = 任一注册商">
                        <Select allowClear style={{ width: 180 }} placeholder="任意"
                            options={registrars.map((r) => ({ value: r.id, label: r.name }))} />
                    </Form.Item>
                </Space>
                <Form.Item label="时间" name="range" rules={[{ required: true, message: '请选择时间' }]}>
                    <DatePicker.RangePicker showTime style={{ width: '100%' }} />
                </Form.Item>
                <Space size={16} align="center" wrap>
                    <Form.Item name="first_year_only" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox>仅首年</Checkbox>
                    </Form.Item>
                    {!firstYearOnly && (
                        <Form.Item label="最多年数" name="max_years" style={{ marginBottom: 0 }} tooltip="留空 = 所选年数全部按特价">
                            <InputNumber min={1} max={10} style={{ width: 100 }} />
                        </Form.Item>
                    )}
                </Space>
            </Form>
        </Modal>
    );
};

const PromotionsTab = ({ registrars, reloadSummary }) => {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState('all');
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState({ open: false, editing: null });
    const [giftPromo, setGiftPromo] = useState(null); // 活动赠品（清单 §4）

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/domain/promotions', { status });
            setRows(r.success && Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            message.error(errText(e));
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const setPromoStatus = async (row, next) => {
        try {
            const r = await api.post('/domain/promotions/status', { id: row.id, status: next });
            if (r.success) { load(); reloadSummary(); } else message.error('操作失败');
        } catch (e) { message.error(errText(e)); }
    };

    const columns = [
        { title: '活动', dataIndex: 'name', render: (v, r) => <Space direction="vertical" size={0}><b>{v}</b><Text type="secondary">.{r.tld} · {OPS[r.operation]}{r.registrar_name ? ` · ${r.registrar_name}` : ''}</Text></Space> },
        {
            title: '价格', key: 'price', render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span><b>{Number(r.price).toFixed(2)}</b> {r.currency}{r.list_price ? <Text delete type="secondary" style={{ marginLeft: 6 }}>{r.list_price}</Text> : null}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.first_year_only ? '仅首年' : (r.max_years ? `最多 ${r.max_years} 年` : '全部年数')}</Text>
                    {r.below_cost?.length > 0 && <Tag color="red" style={{ marginTop: 2 }}>低于成本</Tag>}
                </Space>
            ),
        },
        { title: '时间', key: 'time', render: (_, r) => <Text style={{ fontSize: 12 }}>{r.starts_at}<br />{r.ends_at}</Text> },
        { title: '状态', dataIndex: 'state', width: 90, render: (s) => <Tag color={STATE[s]?.color}>{STATE[s]?.label || s}</Tag> },
        {
            title: '', key: 'act', width: 210, render: (_, r) => (
                <Space size={4}>
                    <Button size="small" onClick={() => setModal({ open: true, editing: r })}>编辑</Button>
                    {r.operation !== 'renew' && <Button size="small" onClick={() => setGiftPromo(r)}>赠品</Button>}
                    {r.status === 'active'
                        ? <Popconfirm title="立即停止该活动？" onConfirm={() => setPromoStatus(r, 'disabled')}><Button size="small" danger>停止</Button></Popconfirm>
                        : <Button size="small" onClick={() => setPromoStatus(r, 'active')}>启用</Button>}
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false}>
            <Space style={{ marginBottom: 16 }} wrap>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ open: true, editing: null })}>新建特价</Button>
                <Select value={status} onChange={setStatus} style={{ width: 120 }} options={[
                    { value: 'all', label: '全部' }, { value: 'current', label: '进行中' },
                    { value: 'upcoming', label: '未开始' }, { value: 'ended', label: '已结束' },
                ]} />
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" size="middle" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 760 }}
                pagination={{ pageSize: 20, hideOnSinglePage: true }} />
            <PromoModal
                open={modal.open}
                editing={modal.editing}
                registrars={registrars}
                onClose={() => setModal({ open: false, editing: null })}
                onSaved={() => { setModal({ open: false, editing: null }); load(); reloadSummary(); }}
            />
            <GiftRulesModal
                open={!!giftPromo}
                scope="promotion"
                refId={giftPromo?.id}
                title={giftPromo ? `活动赠品 · ${giftPromo.name} · .${giftPromo.tld}` : ''}
                onClose={() => setGiftPromo(null)}
            />
        </Card>
    );
};

/* ── 成本审核 ─────────────────────────────────────────────── */
const ReviewTab = ({ reloadSummary }) => {
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/domain/pricing/reviews');
            setRows(r.success && Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            message.error(errText(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const resolve = async (ids, action) => {
        try {
            const r = await api.post('/domain/pricing/reviews/resolve', { ids, action });
            if (r.success) {
                message.success(action === 'approve' ? '已采用新成本' : '已保留原价');
                setSelected([]);
                load();
                reloadSummary();
            } else message.error(r.error || '操作失败');
        } catch (e) { message.error(errText(e)); }
    };

    const fmt = (a, b) => <span>{a ?? '-'} → <b>{b ?? '-'}</b></span>;
    const columns = [
        { title: '后缀', dataIndex: 'tld', render: (v, r) => <span>.{v} <Text type="secondary">{r.registrar_name}</Text></span> },
        { title: '注册成本', key: 'reg', render: (_, r) => fmt(r.cost_price, r.new_cost?.cost_price) },
        { title: '续费成本', key: 'ren', render: (_, r) => fmt(r.renewal_cost, r.new_cost?.renewal_cost) },
        { title: '注册售价', key: 'sp', render: (_, r) => fmt(r.registration_price, r.new_registration_price) },
        { title: '续费售价', key: 'rp', render: (_, r) => fmt(r.renewal_price, r.new_renewal_price) },
        { title: '发现时间', dataIndex: 'detected_at', width: 160 },
        {
            title: '', key: 'act', width: 170, render: (_, r) => (
                <Space size={4}>
                    <Button size="small" type="primary" onClick={() => resolve([r.id], 'approve')}>采用</Button>
                    <Button size="small" onClick={() => resolve([r.id], 'reject')}>保留原价</Button>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false}>
            <Space style={{ marginBottom: 16 }} wrap>
                <Text type="secondary">成本大幅下降的后缀会先保持原售价，等你确认。</Text>
                {selected.length > 0 && (
                    <>
                        <Button type="primary" onClick={() => resolve(selected, 'approve')}>采用所选 ({selected.length})</Button>
                        <Button onClick={() => resolve(selected, 'reject')}>保留原价</Button>
                    </>
                )}
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" size="middle" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 900 }}
                rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
                pagination={{ pageSize: 50, hideOnSinglePage: true }}
                locale={{ emptyText: '没有待审核的价格' }} />
        </Card>
    );
};


/* ── 隐私模式（代理注册） ─────────────────────────────────── */
const PROXY_FIELDS = [
    ['company', '机构名称'], ['first_name', '名'], ['last_name', '姓'], ['address1', '地址'], ['address2', '地址 2'],
    ['city', '城市'], ['state', '州/省'], ['postal_code', '邮编'], ['country', '国家（两位代码）'], ['phone', '电话（+CC.号码）'],
];
const PrivacyTab = () => {
    const [form] = Form.useForm();
    const [data, setData] = useState(null);
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/domain/privacy/settings');
            if (r.success) {
                setData(r.data);
                form.setFieldsValue({ enabled: r.data.enabled, surcharge: r.data.surcharge, proxy: r.data.proxy });
            }
            const l = await api.get('/domain/privacy/log', { limit: 50 });
            if (l.success) setLog(l.data || []);
        } catch (e) {
            message.error(errText(e));
        }
    }, [form]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await api.post('/domain/privacy/settings', v);
            if (r.success) { message.success('已保存'); setData(r.data); } else message.error(r.error || '保存失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card bordered={false}>
            {data && !data.mail_domain_ready && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`转发邮件域 ${data.mail_domain} 未就绪`} />}
            {data && (
                <Space size={16} wrap style={{ marginBottom: 16 }}>
                    <Text>隐私模式域名 <b>{data.domains}</b></Text>
                    <Text type={data.forward_pending ? 'warning' : 'secondary'}>转发未就绪 {data.forward_pending}</Text>
                    <Text type="secondary">不支持的价格行 {data.unsupported_rows}</Text>
                    <Text type="secondary">依法披露 {data.disclosures} 次</Text>
                </Space>
            )}
            <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
                <Space size={16} wrap align="start">
                    <Form.Item name="enabled" label="对新订单开放" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="surcharge" label="附加费（USD/年）" tooltip="隐私模式年价 = 续费价 + 附加费；注册/续费/转入同价。价格行可单独覆盖"
                        rules={[{ required: true }]}><InputNumber min={0} max={1000} step={0.5} precision={2} addonBefore="$" style={{ width: 160 }} /></Form.Item>
                </Space>
                <Card size="small" title="代理实体" extra={<Text type="secondary" style={{ fontSize: 12 }}>注册商登记的注册人，上线前替换为真实资料</Text>} style={{ marginBottom: 16 }}>
                    {PROXY_FIELDS.map(([k, label]) => (
                        <Form.Item key={k} name={['proxy', k]} label={label} style={{ marginBottom: 8 }}
                            rules={['address2', 'state'].includes(k) ? [] : [{ required: true, message: `请填写${label}` }]}>
                            {k === 'country' ? <CountryCodeInput /> : <Input />}
                        </Form.Item>
                    ))}
                    <Text type="secondary" style={{ fontSize: 12 }}>邮箱：每个域名自动生成 p-xxxx@{data?.mail_domain || 'privacy.yeap.cloud'}，转发到用户账户邮箱</Text>
                </Card>
                <Button type="primary" loading={saving} onClick={save}>保存</Button>
            </Form>
            <Typography.Title level={5} style={{ marginTop: 24 }}>披露 / 滥用记录</Typography.Title>
            <Table rowKey="id" size="small" dataSource={log} pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 720 }}
                columns={[
                    { title: '时间', dataIndex: 'created_at', width: 160 },
                    { title: '域名', dataIndex: 'domain' },
                    { title: '类型', dataIndex: 'kind', width: 90, render: (k) => (k === 'disclosure' ? <Tag color="red">依法披露</Tag> : <Tag>滥用备注</Tag>) },
                    { title: '文书编号', dataIndex: 'order_ref', render: (v, r) => v ? `${v}${r.authority ? ` · ${r.authority}` : ''}` : '-' },
                    { title: '理由', dataIndex: 'reason', ellipsis: true },
                    { title: '操作人', dataIndex: 'admin_name', width: 100 },
                ]} />
        </Card>
    );
};

const DomainPricing = () => {
    const [settings, setSettings] = useState(null);
    const [registrars, setRegistrars] = useState([]);

    const loadSettings = useCallback(async () => {
        try {
            const r = await api.get('/domain/pricing/settings');
            if (r.success) setSettings(r.data);
        } catch (e) {
            message.error(errText(e));
        }
    }, []);

    useEffect(() => {
        loadSettings();
        (async () => {
            try {
                const r = await registrarService.getRegistrars();
                const list = Array.isArray(r?.data) ? r.data : (Array.isArray(r?.data?.list) ? r.data.list : []);
                setRegistrars(list.filter((x) => x.is_active || x.enabled));
            } catch (_) { setRegistrars([]); }
        })();
    }, [loadSettings]);

    return (
        <div>
            <PageHeader title="域名定价" />
            <Tabs
                items={[
                    { key: 'policy', label: '加价策略', children: <PolicyTab settings={settings} reload={loadSettings} /> },
                    { key: 'promo', label: `特价活动${settings?.active_promotions ? ` (${settings.active_promotions})` : ''}`, children: <PromotionsTab registrars={registrars} reloadSummary={loadSettings} /> },
                    { key: 'review', label: `成本审核${settings?.pending_review ? ` (${settings.pending_review})` : ''}`, children: <ReviewTab reloadSummary={loadSettings} /> },
                    { key: 'privacy', label: '隐私模式', children: <PrivacyTab /> },
                ]}
            />
        </div>
    );
};

export default DomainPricing;
